/**
 * Prevents duplicate on-chain creates (curve launches / presales) when a
 * previous createLaunch/createPresale attempt is stuck or its outcome is
 * ambiguous (e.g. a receipt-wait timeout after the tx may have already
 * landed). Without this, the create button re-enabling after a timeout lets
 * the user resubmit the same project and mint two tokens.
 *
 * The lock is keyed by wallet + kind + name + ticker and only releases early
 * on a *definitive* outcome (success, user-rejected, or a simulation
 * revert that never broadcast). Ambiguous errors (timeouts, unknown RPC
 * failures) leave the lock in place until it naturally expires.
 */

const STORAGE_KEY = 'penguin-pad-create-locks'
export const CREATE_LOCK_MS = 3 * 60 * 1000
export const PENGUIN_PAD_BRAND_WALLET = '0x7937De50A20602B2564D2162b65ad4a46a191923'
/** PAD impersonator shown as 0x64Ef…c2d4 on the token page. */
export const BLACKLISTED_CREATOR_PREFIX = '0x64ef'
export const BLACKLISTED_CREATOR_SUFFIX = 'c2d4'

function compactBrandName(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '')
}

function compactTicker(value: string): string {
  return value.trim().toUpperCase().replace(/^\$+/, '')
}

function nameWords(value: string): string[] {
  return value.trim().toLowerCase().split(/[^a-z0-9]+/).filter(Boolean)
}

export function isPenguinPadBrandName(name: string, ticker: string): boolean {
  const compactName = compactBrandName(name)
  const tickerKey = compactTicker(ticker)
  return compactName === 'penguinpad'
    || compactName === 'pad'
    || compactName.endsWith('pad')
    || nameWords(name).includes('pad')
    || tickerKey === 'PP'
    || tickerKey === 'PAD'
    || tickerKey === 'PENGUINPAD'
}

export function assertPenguinPadBrandAllowed(name: string, ticker: string, wallet?: string) {
  if (!isPenguinPadBrandName(name, ticker))
    return
  if (wallet?.trim().toLowerCase() === PENGUIN_PAD_BRAND_WALLET.toLowerCase())
    return
  throw new Error('Pad, Penguin Pad, PenguinPad, $PAD, $PenguinPad, and $PP can only be created by the Penguin Pad wallet.')
}

export function isBlacklistedCreator(wallet?: string | null): boolean {
  const address = wallet?.trim().toLowerCase() || ''
  if (!address.startsWith('0x') || address.length !== 42)
    return false
  return address.startsWith(BLACKLISTED_CREATOR_PREFIX) && address.endsWith(BLACKLISTED_CREATOR_SUFFIX)
}

export function assertCreatorAllowed(wallet?: string) {
  if (!isBlacklistedCreator(wallet))
    return
  throw new Error('This wallet is blocked from creating launches on Penguin Pad.')
}

export type CreateLockKind = 'curve' | 'presale'

interface LockEntry {
  key: string
  startedAt: number
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function isLockEntry(value: unknown): value is LockEntry {
  return Boolean(value)
    && typeof value === 'object'
    && typeof (value as LockEntry).key === 'string'
    && typeof (value as LockEntry).startedAt === 'number'
}

function readLocks(): LockEntry[] {
  if (!canUseStorage())
    return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw)
      return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isLockEntry) : []
  }
  catch {
    return []
  }
}

function writeLocks(locks: LockEntry[]) {
  if (!canUseStorage())
    return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(locks))
  }
  catch {
    // Ignore quota / private-mode storage failures.
  }
}

function lockKey(kind: CreateLockKind, creator: string, name: string, ticker: string): string {
  return [kind, creator.trim().toLowerCase(), name.trim().toLowerCase(), ticker.trim().toLowerCase()].join(':')
}

export function acquireCreateLock(
  kind: CreateLockKind,
  creator: string,
  name: string,
  ticker: string,
): { ok: true } | { ok: false, message: string } {
  const key = lockKey(kind, creator, name, ticker)
  const now = Date.now()
  const active = readLocks().filter(entry => now - entry.startedAt < CREATE_LOCK_MS)
  if (active.some(entry => entry.key === key)) {
    writeLocks(active)
    const label = kind === 'curve' ? 'launch' : 'presale'
    return {
      ok: false,
      message: `A ${label} named "${name.trim()}" (${ticker.trim().toUpperCase()}) is already being created from this wallet. Give it a minute to confirm, check My Colony, then try again.`,
    }
  }
  active.push({ key, startedAt: now })
  writeLocks(active)
  return { ok: true }
}

export function releaseCreateLock(kind: CreateLockKind, creator: string, name: string, ticker: string) {
  const key = lockKey(kind, creator, name, ticker)
  const now = Date.now()
  writeLocks(readLocks().filter(entry => entry.key !== key && now - entry.startedAt < CREATE_LOCK_MS))
}
